import { useState, useCallback } from 'react';

const hasIdentity = (identifier) => identifier !== undefined && identifier !== null;

/**
 * Hook for managing document selection state
 * Supports two modes:
 * - 'object': Stores full document objects in selection array
 * - 'key': Stores only keys (e.g., 'id' or 'filename') in selection array
 *
 * @param {Object} options Configuration options
 * @param {Array} options.initialSelected Initial selection array
 * @param {('object'|'key')} options.mode Selection storage mode (default: 'object')
 * @param {string} options.keyName Key name to use in 'key' mode (default: 'id')
 * @param {Function} options.getKeyFn Custom function to extract the identity of a document (both modes)
 * @returns {Object} Selection state and functions
 */
export function useDocumentSelection({
  initialSelected = [],
  mode = 'object',
  keyName = 'id',
  getKeyFn,
} = {}) {
  const [selected, setSelected] = useState(initialSelected);

  /**
   * Extract the identifier from a document based on mode
   */
  const getIdentifier = useCallback(
    (document) => {
      if (getKeyFn) {
        return getKeyFn(document);
      }
      if (mode === 'key') {
        // nullish check, not `||`: an empty string or 0 is a legitimate key and
        // must not fall through to an unrelated attachment
        return document[keyName] ?? document.attachments?.[0];
      }
      return document.id;
    },
    [mode, keyName, getKeyFn],
  );

  /**
   * Build a predicate matching already-selected entries against a document.
   * Documents can legitimately have no identifier (id/filename/attachments are
   * all optional on the generated models) and those must never match each
   * other: object mode falls back to reference identity, while key mode - which
   * has no key to store - matches nothing at all.
   */
  const matchesDocument = useCallback(
    (document) => {
      const identifier = getIdentifier(document);

      if (mode === 'object') {
        if (!hasIdentity(identifier)) {
          return (item) => item === document;
        }
        return (item) => getIdentifier(item) === identifier;
      }

      if (!hasIdentity(identifier)) {
        return () => false;
      }
      return (item) => item === identifier;
    },
    [mode, getIdentifier],
  );

  /**
   * Toggle document selection (add if not present, remove if present)
   */
  const toggleDocument = useCallback(
    (document) => {
      const identifier = getIdentifier(document);
      const matches = matchesDocument(document);

      setSelected((prev) => {
        const isSelected = prev.some(matches);

        if (isSelected) {
          // Remove from selection
          return prev.filter((item) => !matches(item));
        }

        if (mode !== 'object' && !hasIdentity(identifier)) {
          // Nothing to store: an `undefined` entry would collapse every keyless
          // document into the same selection slot and submit a null attachment
          return prev;
        }

        // Add to selection
        const valueToAdd = mode === 'object' ? document : identifier;
        return [...prev, valueToAdd];
      });
    },
    [mode, getIdentifier, matchesDocument],
  );

  /**
   * Check if a document is selected
   */
  const isDocumentSelected = useCallback(
    (document) => selected.some(matchesDocument(document)),
    [selected, matchesDocument],
  );

  /**
   * Get the index of a document in the selection array
   * Returns -1 if not found
   */
  const getDocumentIndex = useCallback(
    (document) => selected.findIndex(matchesDocument(document)),
    [selected, matchesDocument],
  );

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelected([]);
  }, []);

  /**
   * Select multiple documents at once (already-selected documents are skipped,
   * so counts and the "#N" order badges stay in sync)
   */
  const selectMultiple = useCallback(
    (documents) => {
      setSelected((prev) => {
        const next = [...prev];
        documents.forEach((document) => {
          const identifier = getIdentifier(document);
          const matches = matchesDocument(document);
          if (next.some(matches)) return;
          if (mode !== 'object' && !hasIdentity(identifier)) return;
          next.push(mode === 'object' ? document : identifier);
        });
        return next;
      });
    },
    [mode, getIdentifier, matchesDocument],
  );

  return {
    selected,
    setSelected,
    toggleDocument,
    isDocumentSelected,
    getDocumentIndex,
    clearSelection,
    selectMultiple,
  };
}

export default useDocumentSelection;
